import React from 'react';

import PropTypes from 'prop-types';

import {
    memoize,
} from 'wanderu-utils';

import ResizeObserver from './ResizeObserver';

import breakpoints from 'wanderu-css/mediaQueries.scss';

const DEFAULT_QUERIES = {
    isSmallScreen: {
        maxWidth: parseInt(breakpoints.maxWidthSmallScreen, 10),
    },
    isMediumScreen: {
        minWidth: parseInt(breakpoints.minWidthMediumScreen, 10),
    },
    isLargeScreen: {
        minWidth: parseInt(breakpoints.minWidthLargeScreen, 10),
    },
};

function _updateQueries(width, height, queries) {
    if (!queries) {
        return [width, height];
    }

    return Object.entries(queries).reduce((obj, [key, queryParams]) => ({
        ...obj,
        [key]: _testQuery(width, queryParams),
    }), {});
}

function _testQuery(width, queryParams) {
    let { minWidth, maxWidth } = queryParams;
    maxWidth = parseInt(maxWidth, 10) || Infinity;
    minWidth = parseInt(minWidth, 10) || 0;

    return minWidth <= width && width <= maxWidth;
}

function _isEqual(queryResultsA, queryResultsB) {
    if (Object.keys(queryResultsA).length !== Object.keys(queryResultsB).length) {
        return false;
    }

    return Object.entries(queryResultsA).every(([key, value]) => {
        return value === queryResultsB[key];
    });
}

const _memoizedUpdateQueries = memoize(_updateQueries);

export default class ElementQuery extends React.PureComponent {
    static propTypes = {
        dimensionsOnly: PropTypes.bool,
        element: PropTypes.object,
        onChange: PropTypes.func,
        queries: PropTypes.object,
        passAsProps: PropTypes.bool,
    };

    static defaultProps = {
        element: null,
        onChange() {},
        passAsProps: true,
        queries: DEFAULT_QUERIES,
        dimensionsOnly: false,
    }

    constructor(props) {
        super(props);

        this.state = {
            queryResults: this._getInitialState(),
        };
    }

    componentDidMount() {
        if (!this.state.queryResults) {
            this.setState(() => ({ queryResults: this._getInitialState() }));
        } else {
            this.props.onChange(this.state.queryResults);
        }

        this.resizeObserver = new ResizeObserver({
            element: this.props.element || this.rootEl.parentElement,
            onChange: (width, height) => {
                window.requestAnimationFrame(() => {
                    let queryResults = { width, height };
                    if (!this.props.dimensionsOnly) {
                        queryResults = this._calculateQueries(width, height);
                    }

                    const isEqual = _isEqual(this.state.queryResults, queryResults);
                    if (isEqual) {
                        return;
                    }

                    this.props.onChange(queryResults);
                    this.setState(() => ({ queryResults }));
                });
            },
        });

        this.resizeObserver.observe();
    }

    componentWillUnmount() {
        this.resizeObserver.disconnect();
    }

    _getInitialState() {
        const element = this.props.element || (this.rootEl && this.rootEl.parentNode);
        if (!element) {
            return;
        }

        const computedStyles = window.getComputedStyle(element);
        const width = this._getNumber(computedStyles.width);
        const height = this._getNumber(computedStyles.height);
        return this._calculateQueries(width, height);
    }

    _getNumber(numberStr) {
        const number = numberStr.replace(/[^0-9.]/g, '');
        if (!number) {
            return 0;
        }
        return parseFloat(number);
    }

    _calculateQueries = (width, height) => {
        const rounded = [width, height].map(Math.round);
        return _memoizedUpdateQueries(...rounded, this.props.queries);
    }

    _augmentChildren(children) {
        const { passAsProps } = this.props;
        if (!passAsProps) {
            return children;
        }

        return React.Children.map(
            children,
            (child) => {
                if (!child) {
                    return null;
                }

                return React.cloneElement(child, this.state.queryResults);
            },
        );
    }

    render() {
        const { children } = this.props;

        return (
            <React.Fragment>
                <div
                    ref={(node) => this.rootEl = node}
                    style={{ display: 'none' }}
                />
                {this._augmentChildren(children)}
            </React.Fragment>
        );
    }
}
